type Opts = {
  compressThreshold?: number;
  maxFractionDigits?: number;
};

function toSub(n: number) {
  const m = ['₀', '₁', '₂', '₃', '₄', '₅', '₆', '₇', '₈', '₉'];
  return String(n)
    .split('')
    .map((c) => m[+c])
    .join('');
}

export function formatPrice(num: number, sig = 2, opts?: Opts) {
  const { compressThreshold = 3, maxFractionDigits = 50 } = opts || {};

  if (!isFinite(num)) return String(num);
  const neg = num < 0;
  const abs = Math.abs(num);

  /** -------------------------------------------------------
   *  ① 处理 ≥ 1 的情况：直接按小数精度格式化
   * ------------------------------------------------------*/
  if (abs >= 1) {
    const fixed = abs.toFixed(sig);
    let [i, f] = fixed.split('.');

    const intFmt = Number(i).toLocaleString('en-US');
    const result = f ? `${intFmt}.${f}` : `${intFmt}.0`;

    return neg ? '-' + result : result;
  }

  /** -------------------------------------------------------
   *  ② 处理 < 1 的情况：进行前导零压缩逻辑
   * ------------------------------------------------------*/
  const fixed = abs.toFixed(maxFractionDigits);
  let [i, frac] = fixed.split('.');
  frac = frac ?? '';

  // 去掉右侧全部 0（保留用于分析完整的 frac）
  const trimmedRight = frac.replace(/0+$/g, '');
  if (!trimmedRight) return (neg ? '-' : '') + `0.0`;

  // 前导 0 数量
  const leadingZeros = frac.match(/^0*/)?.[0].length ?? 0;

  // 从第一位非零开始取用于四舍五入的部分
  const body = frac.slice(leadingZeros);

  const padded = (body + '0'.repeat(sig + 2)).slice(0, sig + 1);
  const main = padded.slice(0, sig);
  const roundDigit = padded[sig];

  let arr = main.split('').map((n) => +n);

  if (+roundDigit >= 5) {
    let c = 1;
    for (let i = arr.length - 1; i >= 0 && c; i--) {
      const s = arr[i] + c;
      arr[i] = s % 10;
      c = Math.floor(s / 10);
    }
    if (c) {
      return (neg ? '-' : '') + `1.0`; // 全部进位到整数
    }
  }

  // 去除末尾 0
  while (arr[arr.length - 1] === 0) arr.pop();
  if (arr.length === 0) return (neg ? '-' : '') + `0.0`;

  let fracDisplay = '';

  if (leadingZeros >= compressThreshold) {
    fracDisplay = `0${toSub(leadingZeros)}${arr.join('')}`;
  } else {
    fracDisplay = '0'.repeat(leadingZeros) + arr.join('');
  }

  return (neg ? '-' : '') + `0.${fracDisplay}`;
}

/**
 *
 * @param priceChange
 * @returns 0 平 / 2 涨 / 1 跌
 */
export function getPricePositive(priceChange: number): 0 | 1 | 2 {
  if (priceChange > 0) {
    return 2;
  }
  if (priceChange < 0) {
    return 1;
  }
  return 0;
}

function trimFixed(value: number, decimals: number) {
  return value.toFixed(decimals).replace(/\.?0+$/, '');
}

export function formatCompactValue(value?: number | null, prefix = '') {
  if (value === null || value === undefined || Number.isNaN(value)) return '--';

  const absValue = Math.abs(value);
  const sign = value < 0 ? '-' : '';
  const units = [
    { limit: 1e12, suffix: 'T' },
    { limit: 1e9, suffix: 'B' },
    { limit: 1e6, suffix: 'M' },
    { limit: 1e3, suffix: 'K' },
  ];
  const unit = units.find((item) => absValue >= item.limit);

  if (!unit) {
    return `${sign}${prefix}${trimFixed(absValue, 1)}`;
  }

  const scaledValue = absValue / unit.limit;
  // const decimals = scaledValue >= 100 ? 1 : 2;
  return `${sign}${prefix}${trimFixed(scaledValue, 1)}${unit.suffix}`;
}
